import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { parsePhoneNumber, AsYouType } from 'libphonenumber-js';

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  country?: string;
  onCountryChange?: (country: string) => void;
}

const countries = [
  { code: 'GT', name: 'Guatemala', flag: '🇬🇹', dialCode: '+502' },
  { code: 'MX', name: 'México', flag: '🇲🇽', dialCode: '+52' },
  { code: 'US', name: 'Estados Unidos', flag: '🇺🇸', dialCode: '+1' },
  { code: 'CA', name: 'Canadá', flag: '🇨🇦', dialCode: '+1' },
  { code: 'CR', name: 'Costa Rica', flag: '🇨🇷', dialCode: '+506' },
  { code: 'PA', name: 'Panamá', flag: '🇵🇦', dialCode: '+507' },
  { code: 'HN', name: 'Honduras', flag: '🇭🇳', dialCode: '+504' },
  { code: 'SV', name: 'El Salvador', flag: '🇸🇻', dialCode: '+503' },
  { code: 'NI', name: 'Nicaragua', flag: '🇳🇮', dialCode: '+505' },
  { code: 'BZ', name: 'Belice', flag: '🇧🇿', dialCode: '+501' },
  { code: 'ES', name: 'España', flag: '🇪🇸', dialCode: '+34' },
  { code: 'AR', name: 'Argentina', flag: '🇦🇷', dialCode: '+54' },
  { code: 'CO', name: 'Colombia', flag: '🇨🇴', dialCode: '+57' },
];

export function PhoneInput({ 
  value, 
  onChange, 
  placeholder = "Número de teléfono", 
  disabled,
  country = 'GT',
  onCountryChange
}: PhoneInputProps) {
  const [selectedCountry, setSelectedCountry] = useState(country);
  const [displayValue, setDisplayValue] = useState('');

  const selectedCountryData = countries.find(c => c.code === selectedCountry) || countries[0];

  useEffect(() => {
    // Formatear el valor inicial
    if (value) {
      try {
        const phoneNumber = parsePhoneNumber(value);
        if (phoneNumber && phoneNumber.country === selectedCountry) {
          setDisplayValue(phoneNumber.formatNational());
        } else {
          // Si no coincide el país, mostrar solo el número sin formato
          const cleanNumber = value.replace(/^\+\d+\s?/, '');
          setDisplayValue(cleanNumber);
        }
      } catch {
        setDisplayValue(value);
      }
    } else {
      setDisplayValue('');
    }
  }, [value, selectedCountry]);

  const handleCountryChange = (newCountry: string) => {
    setSelectedCountry(newCountry);
    onCountryChange?.(newCountry);
    
    // Si hay un número, intentar reformatearlo para el nuevo país
    if (displayValue) {
      const cleanNumber = displayValue.replace(/\D/g, '');
      if (cleanNumber) {
        const newCountryData = countries.find(c => c.code === newCountry);
        if (newCountryData) {
          try {
            const fullNumber = newCountryData.dialCode + cleanNumber;
            const phoneNumber = parsePhoneNumber(fullNumber);
            if (phoneNumber && phoneNumber.isValid()) {
              const formatted = phoneNumber.formatNational();
              setDisplayValue(formatted);
              onChange(phoneNumber.format('E.164'));
            }
          } catch {
            // Si no se puede parsear, mantener el número actual
          }
        }
      }
    }
  };

  const handleInputChange = (inputValue: string) => {
    try {
      // Usar AsYouType para formatear mientras escribe
      const formatter = new AsYouType(selectedCountry as any);
      const formatted = formatter.input(inputValue);
      
      setDisplayValue(formatted);
      
      // Intentar parsear el número completo
      const fullNumber = selectedCountryData.dialCode + inputValue.replace(/\D/g, '');
      const phoneNumber = parsePhoneNumber(fullNumber);
      
      if (phoneNumber && phoneNumber.isValid()) {
        onChange(phoneNumber.format('E.164'));
      } else {
        // Guardar el número con código de país aunque no esté completo
        onChange(fullNumber);
      }
    } catch (error) {
      // Si hay error, solo actualizar el display
      setDisplayValue(inputValue);
      const cleanInput = inputValue.replace(/\D/g, '');
      if (cleanInput) {
        onChange(selectedCountryData.dialCode + cleanInput);
      } else {
        onChange('');
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    
    try {
      // Intentar detectar el país del número pegado
      const phoneNumber = parsePhoneNumber(pastedText);
      if (phoneNumber && phoneNumber.country) {
        const countryCode = phoneNumber.country;
        const countryData = countries.find(c => c.code === countryCode);
        
        if (countryData) {
          setSelectedCountry(countryCode);
          onCountryChange?.(countryCode);
          setDisplayValue(phoneNumber.formatNational());
          onChange(phoneNumber.format('E.164'));
          return;
        }
      }
    } catch {
      // Si no se puede parsear, tratar como número local
    }
    
    // Si no se detectó país o hubo error, tratar como número del país actual
    const cleanNumber = pastedText.replace(/\D/g, '');
    if (cleanNumber) {
      handleInputChange(cleanNumber);
    }
  };

  return (
    <div className="flex">
      <Select value={selectedCountry} onValueChange={handleCountryChange} disabled={disabled}>
        <SelectTrigger className="w-[140px] rounded-r-none border-r-0">
          <SelectValue>
            <div className="flex items-center gap-2">
              <span>{selectedCountryData.flag}</span>
              <span className="text-sm">{selectedCountryData.dialCode}</span>
            </div>
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="bg-popover z-50">
          {countries.map((country) => (
            <SelectItem key={country.code} value={country.code}>
              <div className="flex items-center gap-2">
                <span>{country.flag}</span>
                <span>{country.dialCode}</span>
                <span className="text-muted-foreground text-sm">{country.name}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      
      <Input
        type="tel"
        placeholder={placeholder}
        value={displayValue}
        onChange={(e) => handleInputChange(e.target.value)}
        onPaste={handlePaste}
        disabled={disabled}
        className="rounded-l-none flex-1"
      />
    </div>
  );
}